import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import {
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronsLeft,
  FastForward,
  HelpCircle,
} from "lucide-react"
import { PATH_MAP } from "../../configuration/PathMap"
import { Permissions, PermissionsType } from "../../Enum/PermissionsType"
import { useEmployerLogged } from "../../context/useEmployeeContext"
import { useMenuMobileContext } from "../../context/useMenuMobileContext"

type OnboardingStep = {
  id: string
  path: string
  permission?: PermissionsType
  onboardingStep?: string
  opensCreate?: boolean
}

type Props = {
  open: boolean
  onClose: () => void
}

export const OWNER_ONBOARDING_START_EVENT = "start-owner-onboarding-first-step"

const buildStepSearch = (step: OnboardingStep) => {
  if (!step.onboardingStep && !step.opensCreate) {
    return ""
  }

  const searchParams = new URLSearchParams()

  if (step.onboardingStep) {
    searchParams.set("onboardingStep", step.onboardingStep)
  }

  if (step.opensCreate) {
    searchParams.set("onboardingCreate", "true")
  }

  return `?${searchParams.toString()}`
}

const closeOnboardingPopups = () => {
  window.dispatchEvent(new Event("close-owner-onboarding-popups"))
}

const expandedMobileStepIds = new Set(["navigation_desktop", "navigation_mobile", "kitchen_bar"])

const shouldExpandMobileStep = (step: OnboardingStep) => expandedMobileStepIds.has(step.id)

const onboardingSteps: OnboardingStep[] = [
  {
    id: "company_data",
    path: PATH_MAP.COMPANY_PAGE,
    permission: Permissions.CanEditeCompany,
    onboardingStep: "company-data",
  },
  {
    id: "social_media",
    path: PATH_MAP.COMPANY_PAGE,
    permission: Permissions.CanEditeCompany,
    onboardingStep: "social-media",
  },
  {
    id: "whatsapp_support",
    path: PATH_MAP.COMPANY_PAGE,
    permission: Permissions.CanEditeCompany,
    onboardingStep: "whatsapp-support",
  },
  {
    id: "public_menu",
    path: PATH_MAP.COMPANY_PAGE,
    permission: Permissions.CanEditeCompany,
    onboardingStep: "public-menu",
  },
  {
    id: "public_menu_customization",
    path: PATH_MAP.COMPANY_PAGE,
    permission: Permissions.CanEditeCompany,
    onboardingStep: "public-menu-customization",
  },
  {
    id: "public_menu_qr",
    path: PATH_MAP.COMPANY_PAGE,
    permission: Permissions.CanEditeCompany,
    onboardingStep: "public-menu-qr",
  },
  {
    id: "store_groups",
    path: PATH_MAP.STORE_GROUPS_PAGE,
    permission: Permissions.CanEditeCompany,
  },
  {
    id: "checklists",
    path: PATH_MAP.CHECKLISTS_PAGE,
    permission: Permissions.CanManageChecklists,
  },
  {
    id: "company_modules",
    path: PATH_MAP.COMPANY_PAGE,
    permission: Permissions.CanEditeCompany,
    onboardingStep: "modules",
  },
  {
    id: "pickup_address",
    path: PATH_MAP.COMPANY_PAGE,
    permission: Permissions.CanEditeCompany,
    onboardingStep: "address",
  },
  {
    id: "delivery_shipping",
    path: PATH_MAP.COMPANY_PAGE,
    permission: Permissions.CanEditeCompany,
    onboardingStep: "shipping",
  },
  { id: "schedule", path: PATH_MAP.COMPANY_PAGE, permission: Permissions.CanEditeCompany, onboardingStep: "schedule" },
  { id: "vacation", path: PATH_MAP.COMPANY_PAGE, permission: Permissions.CanEditeCompany, onboardingStep: "vacation" },
  {
    id: "operational",
    path: PATH_MAP.COMPANY_PAGE,
    permission: Permissions.CanEditeCompany,
    onboardingStep: "operational",
  },
  { id: "billing", path: PATH_MAP.COMPANY_PAGE, permission: Permissions.CanEditeCompany, onboardingStep: "billing" },
  { id: "plans", path: PATH_MAP.PLANS_PAGE, permission: Permissions.CanEditeCompany },
  { id: "whatsapp_agent", path: PATH_MAP.WHATSAPP_AUTOMATION_PAGE, permission: Permissions.CanEditeCompany },
  { id: "whatsapp_crm", path: PATH_MAP.WHATSAPP_CRM_PAGE, permission: Permissions.CanAnswerWhatsApp },
  { id: "whatsapp_official", path: PATH_MAP.WHATSAPP_OFFICIAL_PAGE, permission: Permissions.CanEditeCompany },
  { id: "resposta_ai_integration", path: PATH_MAP.RESPOSTA_AI_INTEGRATION_PAGE, permission: Permissions.CanEditeCompany },
  { id: "ai_integration", path: PATH_MAP.AI_INTEGRATION_PAGE, permission: Permissions.CanViewProfit },
  { id: "fiscal_integration", path: PATH_MAP.FISCAL_INTEGRATION_PAGE, permission: Permissions.CanEditeCompany },
  { id: "fiscal_imports", path: PATH_MAP.FISCAL_IMPORTS_PAGE, permission: Permissions.CanEditeCompany },
  { id: "navigation_desktop", path: PATH_MAP.COMMANDS_PAGE, permission: Permissions.CanEditeOrders },
  { id: "navigation_mobile", path: PATH_MAP.COMMANDS_PAGE, permission: Permissions.CanEditeOrders },
  { id: "kitchen_bar", path: PATH_MAP.COMMANDS_PAGE, permission: Permissions.CanEditeKitchen },
  { id: "commands", path: PATH_MAP.COMMANDS_PAGE, permission: Permissions.CanEditeOrders },
  { id: "commands_create", path: PATH_MAP.COMMANDS_PAGE, permission: Permissions.CanEditeOrders, opensCreate: true },
  { id: "manual_external_orders", path: PATH_MAP.MANUAL_EXTERNAL_ORDERS_PAGE, permission: Permissions.CanEditeOrders },
  {
    id: "manual_external_orders_create",
    path: PATH_MAP.MANUAL_EXTERNAL_ORDERS_PAGE,
    permission: Permissions.CanEditeOrders,
    opensCreate: true,
  },
  { id: "tables", path: PATH_MAP.TABLE_PAGE, permission: Permissions.CanEditeTables },
  { id: "tables_create", path: PATH_MAP.TABLE_PAGE, permission: Permissions.CanEditeTables, opensCreate: true },
  { id: "customers", path: PATH_MAP.CUSTOMER_PAGE, permission: Permissions.CanEditeClients },
  { id: "customers_create", path: PATH_MAP.CUSTOMER_PAGE, permission: Permissions.CanEditeClients, opensCreate: true },
  { id: "customer_exit_check", path: PATH_MAP.CUSTOMER_EXIT_CHECK_PAGE, permission: Permissions.CanEditeClients },
  { id: "reserved_orders", path: PATH_MAP.RESERVED_ORDERS_PAGE, permission: Permissions.CanEditeOrders },
  {
    id: "restaurant_reservations",
    path: PATH_MAP.RESTAURANT_RESERVATIONS_PAGE,
    permission: Permissions.CanEditeReservations,
  },
  {
    id: "reservation_areas",
    path: PATH_MAP.RESTAURANT_RESERVATION_AREAS_PAGE,
    permission: Permissions.CanEditeReservations,
  },
  { id: "my_time_clock", path: PATH_MAP.MY_TIME_CLOCK_PAGE },
  { id: "time_clock", path: PATH_MAP.TIME_CLOCK_PAGE, permission: Permissions.CanEditeTimeClock },
  { id: "rodizio", path: PATH_MAP.RODIZIO_PAGE, permission: Permissions.CanEditeCompany },
  { id: "rodizio_metrics", path: PATH_MAP.RODIZIO_METRICS_PAGE, permission: Permissions.CanViewProfit },
  { id: "daily_income", path: PATH_MAP.DAILY_INCOME_PAGE, permission: Permissions.CanViewProfit },
  { id: "monthly_income", path: PATH_MAP.MONTH_INCOME_PAGE, permission: Permissions.CanViewProfit },
  { id: "employee_metrics", path: PATH_MAP.EMPLOYEE_METRICS_PAGE, permission: Permissions.CanViewProfit },
  { id: "payroll_summary", path: PATH_MAP.PAYROLL_SUMMARY_PAGE, permission: Permissions.CanViewProfit },
  { id: "menu_metrics", path: PATH_MAP.MENU_METRICS_PAGE, permission: Permissions.CanViewProfit },
  { id: "meals", path: PATH_MAP.MEALS_PAGE, permission: Permissions.CanEditeMeals },
  { id: "meals_create", path: PATH_MAP.MEALS_PAGE, permission: Permissions.CanEditeMeals, opensCreate: true },
  { id: "drinks", path: PATH_MAP.DRINKS_PAGE, permission: Permissions.CanEditeDrinks },
  { id: "drinks_create", path: PATH_MAP.DRINKS_PAGE, permission: Permissions.CanEditeDrinks, opensCreate: true },
  { id: "combos", path: PATH_MAP.COMBO_PAGE, permission: Permissions.CanEditeCombos },
  { id: "combos_create", path: PATH_MAP.COMBO_PAGE, permission: Permissions.CanEditeCombos, opensCreate: true },
  { id: "categories", path: PATH_MAP.CATEGORY_PAGE, permission: Permissions.CanEditeCategories },
  {
    id: "categories_create",
    path: PATH_MAP.CATEGORY_PAGE,
    permission: Permissions.CanEditeCategories,
    opensCreate: true,
  },
  { id: "additionals", path: PATH_MAP.ADDTIONAL_PAGE, permission: Permissions.CanEditeAddtionals },
  {
    id: "additionals_create",
    path: PATH_MAP.ADDTIONAL_PAGE,
    permission: Permissions.CanEditeAddtionals,
    opensCreate: true,
  },
  { id: "selections", path: PATH_MAP.SELECTION_PAGE, permission: Permissions.CanEditeAddtionals },
  {
    id: "selections_create",
    path: PATH_MAP.SELECTION_PAGE,
    permission: Permissions.CanEditeAddtionals,
    opensCreate: true,
  },
  { id: "promotions", path: PATH_MAP.PROMOTIONS_PAGE, permission: Permissions.CanEditePromotions },
  {
    id: "promotions_create",
    path: PATH_MAP.PROMOTIONS_PAGE,
    permission: Permissions.CanEditePromotions,
    opensCreate: true,
  },
  { id: "awards", path: PATH_MAP.AWARDS_PAGE, permission: Permissions.CanEditeAwards },
  { id: "awards_create", path: PATH_MAP.AWARDS_PAGE, permission: Permissions.CanEditeAwards, opensCreate: true },
  { id: "extra_services", path: PATH_MAP.SERVICES_PAGE, permission: Permissions.CanEditeExtraService },
  {
    id: "extra_services_create",
    path: PATH_MAP.SERVICES_PAGE,
    permission: Permissions.CanEditeExtraService,
    opensCreate: true,
  },
  { id: "coupons", path: PATH_MAP.COUPONS_PAGE, permission: Permissions.CanEditeCoupons },
  { id: "coupons_create", path: PATH_MAP.COUPONS_PAGE, permission: Permissions.CanEditeCoupons, opensCreate: true },
  { id: "notices", path: PATH_MAP.NOTICES_PAGE, permission: Permissions.CanEditeNotices },
  { id: "notices_create", path: PATH_MAP.NOTICES_PAGE, permission: Permissions.CanEditeNotices, opensCreate: true },
  { id: "employees", path: PATH_MAP.EMPLOYEES_PAGE, permission: Permissions.CanEditeEmployee },
  {
    id: "employees_create",
    path: PATH_MAP.EMPLOYEES_PAGE,
    permission: Permissions.CanEditeEmployee,
    opensCreate: true,
  },
  { id: "print_automation", path: PATH_MAP.PRINT_AUTOMATION_PAGE, permission: Permissions.CanEditeCompany },
  { id: "delivery", path: PATH_MAP.COURIER_PARTNERS_PAGE, permission: Permissions.CanEditeCourier },
  {
    id: "delivery_create",
    path: PATH_MAP.COURIER_PARTNERS_PAGE,
    permission: Permissions.CanEditeCourier,
    opensCreate: true,
  },
  { id: "help_center", path: PATH_MAP.HELP_CENTER_PAGE, permission: Permissions.CanEditeCompany },
  { id: "push_notifications", path: PATH_MAP.PUSH_NOTIFICATIONS_PAGE, permission: Permissions.CanEditeCompany },
  { id: "tutorials", path: PATH_MAP.TUTORIALS_PAGE, permission: Permissions.CanEditeCompany },
]

export const OwnerOnboardingTutorial: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation("ownerOnboarding")

  const navigate = useNavigate()

  const location = useLocation()

  const { userEmployee } = useEmployerLogged()

  const { isNotMobile } = useMenuMobileContext()

  const [currentIndex, setCurrentIndex] = useState(0)

  const [isMobileExpanded, setMobileExpanded] = useState(true)

  const availableSteps = useMemo(() => {
    if (!userEmployee?.isMaster) {
      return []
    }

    return onboardingSteps.filter((step) => !step.permission || userEmployee.permissions?.includes(step.permission))
  }, [userEmployee])

  useEffect(() => {
    if (open) {
      setCurrentIndex(0)
      setMobileExpanded(isNotMobile)
    }
  }, [isNotMobile, open])

  useEffect(() => {
    if (!open || availableSteps.length === 0) {
      return
    }

    setCurrentIndex((index) => Math.min(index, availableSteps.length - 1))
  }, [availableSteps.length, open])

  const currentStep = availableSteps[currentIndex]

  const isCurrentStepActive = useMemo(() => {
    if (!currentStep || location.pathname !== currentStep.path) {
      return false
    }

    if (!currentStep.onboardingStep && !currentStep.opensCreate) {
      return true
    }

    const searchParams = new URLSearchParams(location.search)

    const hasMatchingStep =
      !currentStep.onboardingStep || searchParams.get("onboardingStep") === currentStep.onboardingStep

    const hasMatchingCreate = !currentStep.opensCreate || searchParams.get("onboardingCreate") === "true"

    return hasMatchingStep && hasMatchingCreate
  }, [currentStep, location.pathname, location.search])

  const navigateToStep = (step: OnboardingStep, shouldCloseCurrentPopups = true) => {
    if (shouldCloseCurrentPopups) {
      closeOnboardingPopups()
    }

    navigate({
      pathname: step.path,
      search: buildStepSearch(step),
    })

    if (!isNotMobile) {
      setMobileExpanded(shouldExpandMobileStep(step))
    }
  }

  useEffect(() => {
    const handleStartTutorial = () => {
      const firstStep = availableSteps[0]

      if (!firstStep) {
        return
      }

      closeOnboardingPopups()
      setCurrentIndex(0)

      navigate({
        pathname: firstStep.path,
        search: buildStepSearch(firstStep),
      })

      if (!isNotMobile) {
        setMobileExpanded(shouldExpandMobileStep(firstStep))
      }
    }

    window.addEventListener(OWNER_ONBOARDING_START_EVENT, handleStartTutorial)

    return () => window.removeEventListener(OWNER_ONBOARDING_START_EVENT, handleStartTutorial)
  }, [availableSteps, isNotMobile, navigate])

  const handleContinue = () => {
    if (!currentStep) {
      closeOnboardingPopups()
      onClose()

      return
    }

    if (!isCurrentStepActive) {
      navigateToStep(currentStep, false)

      return
    }

    const nextIndex = currentIndex + 1

    if (nextIndex >= availableSteps.length) {
      closeOnboardingPopups()
      onClose()

      return
    }

    const nextStep = availableSteps[nextIndex]
    const shouldCloseCurrentPopups = Boolean(currentStep.opensCreate || currentStep.onboardingStep)

    setCurrentIndex(nextIndex)
    navigateToStep(nextStep, shouldCloseCurrentPopups)
  }

  const handlePrevious = () => {
    const previousIndex = Math.max(currentIndex - 1, 0)
    const previousStep = availableSteps[previousIndex]

    setCurrentIndex(previousIndex)

    if (previousStep) {
      navigateToStep(previousStep)
    }
  }

  const handleCloseTutorial = () => {
    closeOnboardingPopups()
    onClose()
  }

  if (!open || !currentStep || !userEmployee?.isMaster) {
    return null
  }

  const current = currentIndex + 1

  const total = availableSteps.length

  const progress = Math.round((current / total) * 100)

  const isLastStep = currentIndex >= availableSteps.length - 1 && isCurrentStepActive

  const shouldUseCompactMobile = !isNotMobile && !isMobileExpanded

  if (shouldUseCompactMobile) {
    return (
      <div className="pointer-events-none fixed inset-0 z-[980]">
        <div className="pointer-events-auto fixed left-0 top-0 flex w-full overflow-hidden bg-white text-slate-900 shadow-xl ring-2 ring-black">
          <button
            type="button"
            onClick={() => setMobileExpanded(true)}
            className="group flex flex-1 items-center gap-2 px-3 py-3 text-left"
            aria-label={t("show_tip")}
            title={t("show_tip")}
            data-interaction-description-key="interactionDescriptions:components.tutorial.ownerOnboardingTutorial.button.showTip"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[10px] font-black uppercase leading-tight text-orange-700">
                {t("compact_label")}
              </span>

              <span className="block truncate text-xs font-black leading-tight text-slate-900">
                {t("compact_expand_hint")}
              </span>
            </span>

            <span className="mt-1 inline-flex rounded-full bg-orange-200 px-2 py-0.5 text-[12px] font-black text-black">
              {t("step", { current, total })}
            </span>
          </button>

          <button
            type="button"
            onClick={handleContinue}
            className="shrink-0 bg-red-500 px-4 text-sm font-bold uppercase text-black"
            aria-label={t("continue")}
            data-interaction-description-key="interactionDescriptions:components.tutorial.ownerOnboardingTutorial.button.continue"
          >
            <div className="flex items-center gap-1">
              <span>{t("continue")}</span>
              <ArrowRight size={18} />
            </div>
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="pointer-events-none fixed inset-0 z-[980] flex items-end justify-center p-3 md:items-end md:justify-end md:p-5">
      <div
        className={`pointer-events-auto w-full overflow-hidden border-2 border-black bg-white text-slate-900 shadow-2xl ring-2 ring-orange-100 ${
          isNotMobile ? "max-w-[430px] rounded-2xl" : "rounded-t-3xl"
        }`}
      >
        <div className="flex items-start justify-between gap-3 border-b border-orange-200 bg-orange-50 p-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-black px-2.5 py-1 text-[11px] font-black uppercase text-white">
                <CheckCircle2 size={13} />
                {t("badge")}
              </span>

              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-200">
                {t("step", { current, total })}
              </span>
            </div>

            <h2 className="mt-2 text-lg font-black leading-tight text-slate-900 md:text-xl">
              {t(`steps.${currentStep.id}.title`)}
            </h2>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {!isNotMobile && (
              <button
                type="button"
                onClick={() => setMobileExpanded(false)}
                className="inline-flex h-9 items-center justify-center gap-1 rounded-full bg-white px-3 text-xs font-black uppercase text-red-500 ring-2 ring-red-500"
                aria-label={t("hide_tip")}
                data-interaction-description-key="interactionDescriptions:components.tutorial.ownerOnboardingTutorial.button.hideTip"
              >
                <ArrowDownToLine size={18} />
                {t("hide_tip_short")}
              </button>
            )}
          </div>
        </div>

        <div className="p-4">
          <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-red-500 transition-all" style={{ width: `${progress}%` }} />
          </div>

          <p className="text-sm leading-relaxed text-justify font-semibold text-black">
            {t(`steps.${currentStep.id}.description`)}
          </p>

          <div
            className="mt-3 rounded-xl border-2 border-orange-300 bg-white
           px-3 py-2 text-sm font-semibold text-orange-800"
          >
            {t(`steps.${currentStep.id}.action`)}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-[auto_1fr_auto]">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              data-interaction-description-key="interactionDescriptions:components.tutorial.ownerOnboardingTutorial.button.handlePrevious"
            >
              <ArrowLeft size={16} />
              {t("previous")}
            </button>

            <button
              type="button"
              onClick={handleCloseTutorial}
              className="inline-flex items-center justify-center gap-2 rounded-xl ring-2 ring-slate-200 bg-slate-200 px-4 py-2.5 text-sm font-bold text-slate-8000"
              data-interaction-description-key="interactionDescriptions:components.tutorial.ownerOnboardingTutorial.button.handleCloseTutorial"
            >
              <FastForward size={16} />
              {t("skip")}
            </button>

            <button
              type="button"
              onClick={handleContinue}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-500 px-4 py-2.5 text-sm font-black uppercase text-black hover:bg-orange-400"
              data-interaction-description-key="interactionDescriptions:components.tutorial.ownerOnboardingTutorial.button.handleContinue"
            >
              {isLastStep ? t("finish") : t("continue")}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
